<script>
import { useAlert } from 'dashboard/composables';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import ErrorState from 'dashboard/components/widgets/ErrorState.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import PaginationBar from 'dashboard/components/dahreply/PaginationBar.vue';
import { debounce } from '@chatwoot/utils';

export default {
  name: "StaffSelectionTable",
  components: {
    LoadingState,
    ErrorState,
    EmptyState,
    PaginationBar,
  },
  props: {
    initialSelectedIds: {
      type: Array,
      default: () => []
    },
    confirmButtonText: {
      type: String,
      default: 'Confirm'
    },
    branchId: {
      type: String,
      default: null,
    }
  },
  data() {
    return {
      selectedIdsMap: {}, // { staffId: true/false }
      selectAllPage: false, // select all checkbox for current page
      debouncedUpdateQuery: null,
    }
  },
  created() {
    this.debouncedUpdateQuery = debounce((value) => {
      this.$store.dispatch('staffOptions/updateFiltersAndFetch', { q: value });
    }, 300);
  },
  computed: {
    isLoading() {
      return this.$store.state.staffOptions.isLoading || false;
    },
    isError() {
      return this.$store.state.staffOptions.isError || false;
    },
    optionList() {
      return this.$store.state.staffOptions.options || [];
    },
    pagination() {
      return this.$store.getters['staffOptions/formattedPagination'];
    },
    filters() {
      return this.$store.state.staffOptions.filters;
    },
    filterQuery: {
      get() { return this.filters.q; },
      set(value) { this.debouncedUpdateQuery(value); }
    },
    isFilterActive() {
      return this.filters.branch_id || this.filters.q;
    },
    emptyStateMessage() {
      if (this.isFilterActive) return 'No staffs match your current filters.';
      return 'Try creating a new staff to get started.';
    },
    currentPageIds() {
      return this.optionList.map(item => item.staff.id);
    },
    allSelectedPage() {
      return this.currentPageIds.every(id => this.selectedIdsMap[id]);
    },
    selectedCount() {
      return Object.values(this.selectedIdsMap).filter(Boolean).length;
    },
  },
  methods: {
    async fetchOptions() {
      try {
        await this.$store.dispatch('staffOptions/fetchOptions');
      } catch(err) {
        useAlert(err);
        console.error(err);
      }
    },
    async fetchOptionsByBranch(branchId) {
      try {
        await this.$store.dispatch('staffOptions/updateFiltersAndFetch', { branch_id: branchId });
      } catch(err) {
        useAlert(err);
        console.error(err);
      }
    },
    fetchPreviousPage() {
      if (this.pagination.currentPage > 1) {
        let prev = this.pagination.currentPage - 1;
        this.$store.dispatch('staffOptions/updatePaginationAndFetch', { page: prev });
      }
    },
    fetchNextPage() {
      if (this.pagination.currentPage < this.pagination.totalPages) {
        let next = this.pagination.currentPage + 1;
        this.$store.dispatch('staffOptions/updatePaginationAndFetch', { page: next });
      }
    },
    updatePageSize(newSize) {
      this.$store.dispatch('staffOptions/updatePaginationAndFetch', { page: 1, page_size: newSize });
    },
    async clearFilters() {
      await this.$store.dispatch('staffOptions/reset');
      if(this.branchId !== null) {
        await this.fetchOptionsByBranch(this.branchId)
      } else {
        await this.fetchOptions();
      }
    },
    toggleSelect(staffId) {
      this.$set(this.selectedIdsMap, staffId, !this.selectedIdsMap[staffId]);
      this.emitSelection();
    },
    toggleSelectAllPage() {
      const newValue = !this.allSelectedPage;
      this.currentPageIds.forEach(id => {
        this.$set(this.selectedIdsMap, id, newValue);
        this.emitSelection();
      });
    },
    handleConfirm() {
      const selectedIds = Object.keys(this.selectedIdsMap).filter(id => this.selectedIdsMap[id]);
      this.$emit('confirm', selectedIds);
    },
    initializeSelectedIds() {
      this.initialSelectedIds.forEach(id => {
        this.$set(this.selectedIdsMap, id, true);
      });
      this.emitSelection();
    },
    emitSelection() {
      const selectedIds = Object.keys(this.selectedIdsMap).filter(id => this.selectedIdsMap[id]);
      this.$emit('change', selectedIds);
    }
  },
  watch: {
    optionList: {
      handler() {
        this.initializeSelectedIds();
        this.selectAllPage = this.allSelectedPage;
        this.emitSelection();
      },
      immediate: true
    }
  },
  async mounted() {
    await this.$store.dispatch('staffOptions/reset');
    if(this.branchId !== null) {
      await this.fetchOptionsByBranch(this.branchId)
    } else {
      await this.fetchOptions();
    }
  }
}
</script>

<template>
  <div class="w-full">
    <!-- Filters -->
    <div class="w-full flex gap-4 items-end mb-4">
      <input 
        v-model="filterQuery" 
        placeholder="Enter your query here..."
        class="border px-2 py-1.5 border-slate-200 rounded-md flex-1"
      />
      <woot-button color-scheme="secondary" @click="clearFilters" :disabled="!isFilterActive">
        Clear Filters
      </woot-button>
    </div>

    <!-- Pagination -->
    <PaginationBar
      v-if="!isLoading && !isError && optionList.length !== 0"
      :pagination="pagination"
      :loading="isLoading"
      @previous="fetchPreviousPage"
      @next="fetchNextPage"
      @page-size-change="updatePageSize"
    />

    <div class="relative">
      <!-- Table -->
      <div v-if="!isLoading && !isError && optionList.length">
        <table class="w-full border-collapse border border-slate-200 dark:border-slate-700 text-left">
          <thead>
            <tr class="bg-slate-100 dark:bg-slate-800">
              <th class="p-2 border border-slate-200 dark:border-slate-700" style="width: 48px; max-width: 48px; text-align: center;">
                <input type="checkbox" :checked="allSelectedPage" @change="toggleSelectAllPage" />
              </th>
              <th class="p-2 border border-slate-200 dark:border-slate-700">Staff Name</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in optionList"
              :key="item.staff.id"
              class="hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer"
              @click="toggleSelect(item.staff.id)"
            >
              <td class="p-2 border border-slate-200 dark:border-slate-700" style="width: 48px; max-width: 48px; text-align: center;">
                <input
                  type="checkbox"
                  :checked="selectedIdsMap[item.staff.id]"
                  @click.stop
                  @change="toggleSelect(item.staff.id)"
                />
              </td>
              <td class="p-2 border border-slate-200 dark:border-slate-700">
                {{ item.staff.name }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="!isLoading && !isError && optionList.length" class="flex justify-end gap-4 mt-2">
        <p class="text-sm text-slate-600">
          <span class="font-semibold">{{ selectedCount }}</span> selected
        </p>
      </div>

      <!-- Loading/Error/Empty States -->
      <LoadingState v-if="isLoading" message="Loading staffs..." />
      <ErrorState v-else-if="!isLoading && isError" />
      <EmptyState
        v-else-if="!isLoading && !isError && optionList.length === 0"
        title="No Staff Found"
        :message="emptyStateMessage"
      />
    </div>
  </div>
</template>